/* eslint-disable */
/* tslint-disable */
import { inject } from '../di'
import {
    IAnalyticsService,
    Dimension,
    Metric,
    CouponActions,
    CouponSource,
    LoginSource,
    LoyaltyCardSource,
    ShoppingListAction,
    ShoppingListEventContext,
    FamilySyncAction,
    FavouriteAction,
    FavouriteContext,
    LandingPageActions,
    CrossBrowseAction,
    AggregateSourceAction,
    FlyerPlacement,
    TRACKER_BASE,
    LANDING_PAGE_VERSION,
    TrackFlippProps,
    gtag,
} from '../definitions/IAnalyticsService'
import {
    LoyaltyProgramMerchant,
    SearchMode,
    LoyaltyProgram,
    LoyaltyProgramCoupon,
    Experiment,
    AuthProvider,
    PageStatus,
} from '../../definitions/CommonDefinitions'
import { ConfigService, Experience } from '../ConfigService'
import { EVTrackingService } from '../EVTrackingService'
import { Flyer } from '../../definitions/FlyerDefinitions'
import { Coupon } from '../../definitions/CouponDefinitions'
import { LocationService } from '../LocationService'
import { getSessionId } from '../../utils/uuid'

// Private Definitions ------------------------------------------------------->

const SDE_ANALYTIC_MESSAGES = {
    EV: 'flipp_sde_flyer_engagement',
    FlyerOpen: 'flipp_sde_flyer_open',
}

// Public Definitions -------------------------------------------------------->

export type AnalyticsParameters = { [key: string]: string }
export const FLYER_ITEM_CLICK_TYPE = [
    'open',
    'ttm',
    'shareFacebook',
    'shareTwitter',
    'add',
    'shareURL',
    'shareGoogle',
    'sharePinterest',
    'cart',
    'shareEmail',
    'shareSMS',
    'video',
]
export const COUPON_ACTIONS = [
    'open',
    'barcode',
    'add',
    'addMatched',
    'send',
    'shareURL',
    'redeemed',
    'lc_selected',
]

export const LANDING_PAGE_ACTIONS = [
    'start_saving_button',
    'browser_flyers_button',
    'browser_coupons_button',
    'create_sl_button',
    'facebook_button',
    'twitter_button',
    'youtube_button',
    'linkedin_button',
    'flyers_nav_click',
    'coupons_nav_click',
    'sl_nav_click',
    'search_nav_click',
    'back_to_top_click',
    'about_flipp_click',
    'flyer_open',
    'browse_flipp_button',
    'learn_more_button',
    'search_flipp_button',
    'see_more_button',
    'job_openings_button',
    'job_openings_link',
]

type GAPayload = string | { [key: string]: string | number }

type TrackFlippQueueEntry = [string, { [key: string]: string }]
type GAQueueEntry = [string, GAPayload, string]

export abstract class BaseAnalyticsDelegate implements IAnalyticsService {
    @inject config: ConfigService
    @inject evTracker: EVTrackingService
    @inject location: LocationService

    protected nextXBrowseFromOpen: Flyer = null
    protected nextXBrowseFromEngagement: Flyer = null
    protected _accountGuid: string

    private _tagImage: new () => HTMLImageElement
    private _tagIframe: HTMLIFrameElement

    private _queuedEvents: (TrackFlippQueueEntry | GAQueueEntry)[] = []

    constructor() {
        // Fetch the Image constructor from the noreferrer.html document.
        // Image elements made from this constructor will not leak referrer
        // information.
        const iframe = document.createElement('iframe')
        iframe.width = '0'
        iframe.height = '0'
        iframe.tabIndex = -1
        iframe.onload = () => {
            this._tagImage = iframe.contentWindow.Image
        }
        iframe.classList.add('tracker')
        iframe.src = '/' + this.config.BUILD + '/noreferrer.html'
        document.body.appendChild(iframe)

        // Create a sandboxed iframe for tagman.html, which downloads arbitrary
        // JS to do some arbitrary tracking
        this._tagIframe = document.createElement('iframe')
        this._tagIframe.setAttribute('partition', 'tagman')
        this._tagIframe.width = '0'
        this._tagIframe.height = '0'
        this._tagIframe.tabIndex = -1
        this._tagIframe.classList.add('tracker')
        document.body.appendChild(this._tagIframe)
    }

    setSessionIdInGTM() {
        /* Set sessionID in GTM dataLayer */
        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            sessionId: this.sessionId,
        })
    }

    protected get postalCode() {
        return document.documentElement.getAttribute('postalcode')
    }

    get sessionId() {
        return getSessionId()
    }

    get channelId(): number {
        return this.postalCode.length > 5
            ? this.config.FLIPP_CANADA_CHANNEL_ID
            : this.config.FLIPP_US_CHANNEL_ID
    }

    get profileId(): string {
        return null
    }

    protected trackTag() {
        /*
         * Comment out tagman tracking.
         *
         * Google CA coversion pixel was the only thing using tagman. We converted it over to Google
         * Tag Manager as part of FW-781 so this code can be disabled (temporarily?)
         */
        // if (this.isAnalyticsDelayed()) {
        // 	/* Don't send beacons is analytics are delayed */
        // 	// console.log("[FW] Dropped event to trackTag() because analytics are delayed");
        // 	return;
        // }
        // if (isCrawler())
        // return;
        // const queryArgs = this.analyticsParameters();
        // // FIXME: we shouldn't be adding values here (add in impl cases);
        // queryArgs["rnd"] = Math.random().toString().slice(2);
        // queryArgs["t"] = ev;
        // queryArgs["aid"] = "flyers";
        // queryArgs["channel"] = this.channelId.toString();
        // queryArgs["entity_type"] = "publisher";
        // queryArgs["entity_id"] = "flippweb";
        // queryArgs["tagserver"] = this.config.WISHABI_TAGMAN_SERVER;
        // queryArgs["p"] = "flippweb";
        // const uri = "/" + this.config.BUILD + "/tagman.html?" +
        // 		Object.keys(queryArgs).map(function(key) {
        // 		return [encodeURIComponent(key),
        // 						encodeURIComponent(queryArgs[key])].join("=");
        // 		}).join("&");
        // if (this._tagIframe.tagName === "IFRAME")
        // 	this._tagIframe.contentWindow.location.replace(uri);
        // else
        // 	this._tagIframe.src = uri;
    }

    protected async trackFlipp({
        ev,
        options,
        force = false,
        syncGetRequest = false,
        fireTagman = true,
        addAvroKey = false,
        isAvro = false,
    }: TrackFlippProps) {
        if (!(await this.location.isAllowedCountry())) return

        const modifiedOptions = addAvroKey
            ? { ...options, avro: isAvro }
            : options

        if (
            (this.isAnalyticsDelayed() || flipp.status !== PageStatus.Loaded) &&
            !force
        ) {
            /* Don't send beacons if analytics are delayed. Instead, save them in a queue to be sent IF analytics
             * are enabled at a later point
             * Also don't send beacons if the page is not in LOADED state. This is to avoid cases where we were
             * sending beacons/a.wishabi events even when initialization failed and the error page displayed */
            // console.log("[FW] Dropped event to trackFlipp() because analytics are delayed", ev, options);
            this._queuedEvents.push([ev, modifiedOptions])
            return
        }

        const queryArgs = this.analyticsParameters()
        Object.keys(modifiedOptions).forEach(function (key) {
            queryArgs[key] = modifiedOptions[key]
        })

        queryArgs['t'] = ev
        const pathSearch =
            '/track.gif?' +
            Object.keys(queryArgs)
                .map(function (key) {
                    return [
                        encodeURIComponent(key),
                        encodeURIComponent(queryArgs[key]),
                    ].join('=')
                })
                .join('&')

        if (syncGetRequest) {
            const url = this.config.FLIPP_ANALYTICS_SERVER + pathSearch
            const xhr = new XMLHttpRequest()
            xhr.open('GET', url, false)
            xhr.onerror = () => {
                // just skipping
            }
            xhr.send()
        } else {
            new Image().src = this.config.FLIPP_ANALYTICS_SERVER + pathSearch
            if (fireTagman) {
                if (this._tagImage)
                    new this._tagImage().src =
                        this.config.TAGMAN_SERVER + pathSearch + '&p=usflipp2'
            }
        }
    }

    protected trackGA(
        command: string,
        payload: GAPayload,
        tracker = TRACKER_BASE,
        force = false
    ) {
        if (
            (this.isAnalyticsDelayed() || flipp.status !== PageStatus.Loaded) &&
            !force
        ) {
            /* Don't send beacons if analytics are delayed. Instead, save them in a queue to be sent IF analytics
             * are enabled at a later point
             * Also don't send beacons if the page is not in LOADED state. This is to avoid cases where we were
             * sending beacons/a.wishabi events even when initialization failed and the error page displayed */
            // console.log("[FW] Dropped event to trackGA() because analytics are delayed", command, payload, tracker);
            this._queuedEvents.push([command, payload, tracker])
            return
        }

        ga(`${tracker}.${command}`, payload)
    }

    protected generateFlyerIds(
        categoryName: string,
        flyers: Flyer[],
        favMerchants?: number[]
    ) {
        // The analytics team requires the following format for flyer_ids:
        // "f<flyerid>p<0|1>f<flyerid>p<0|1>". e.g. two flyers with flyer
        // ids 555 (premium) and 777 (non-premium) would be converted to
        // the following string: "f555p1f777p0".
        // ------------------------------------------------------------------
        let flyerIds: string[] = []

        if (categoryName === 'favorites') {
            flyerIds = flyers
                .filter(
                    (flyer) => favMerchants.indexOf(flyer.merchantId) !== -1
                )
                .map(
                    (flyer) =>
                        'f' + flyer.id + 'p' + (flyer.premium ? '1' : '0')
                )
        } else {
            flyerIds = flyers
                .filter((flyer) => flyer.categories.indexOf(categoryName) >= 0)
                .map(
                    (flyer) =>
                        'f' + flyer.id + 'p' + (flyer.premium ? '1' : '0')
                )
        }

        return flyerIds
    }

    protected combineFlyerAnalytics(
        flyer: Flyer,
        options: { [key: string]: string } = {}
    ) {
        options['analytics_payload'] = flyer.analyticsPayload
        return options
    }

    /* Delegate-specific analytics parameters should be supplied by the implementing classes */
    abstract analyticsParameters(): AnalyticsParameters

    /* Flag to control whether or not analytic beacons should be delayed. Any beacons fired
     * while true will be droped (ie: not sent) */
    abstract isAnalyticsDelayed(): boolean
    originFlyerId(): string {
        return ''
    }

    siteLoaded() {
        /* Callback for when the site is fully loaded. Once we recieve this message, we can release
         * any beacons that were queued. If we're in distribution and analytics are delayed, we still
         * need to wait for that event before proceeding */

        if (flipp.status === PageStatus.Loaded && !this.isAnalyticsDelayed()) {
            this.sendQueuedEvents()
        }
    }

    protected sendQueuedEvents() {
        /* Sends all pending events in the event queue to either GA or a.wishabi depending on the type */
        this._queuedEvents.forEach((event) => {
            if (event.length === 2) {
                const evt = event as TrackFlippQueueEntry
                const trackFlippProps: TrackFlippProps = {
                    ev: evt[0],
                    options: evt[1],
                }
                this.trackFlipp(trackFlippProps)
            } else if (event.length === 3) {
                const evt = event as GAQueueEntry
                this.trackGA(evt[0], evt[1], evt[2])
            }
        })
        this._queuedEvents = []
    }

    isFreshFlyer() {
        /* Should never be called */ return false
    }

    setPostalCode(postalCode: string) {
        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            postalCode: postalCode,
        })
    }

    setLocale(locale: string) {
        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            locale: locale,
        })
    }

    setAccountGuid(guid: string) {
        // Push to dataLayer?
        this._accountGuid = guid
    }

    getAccountGuid() {
        return this._accountGuid
    }

    trackPageView() {
        const pageView = dataLayer.find((layer) => layer.originalLocation)

        this.trackGA('set', {
            page: window.location.pathname + window.location.search,
            title: document.title,
        })
        this.trackGA('send', 'pageview')

        dataLayer.push({
            originalLocation: pageView.originalLocation,
            event: 'pageview',
            page: window.location.pathname + window.location.search,
            title: document.title,
        })
    }

    trackFlyerOpen(
        flyer: Flyer,
        sourceAction: AggregateSourceAction,
        xbrowseId?: string
    ) {
        const id = flyer.id.toString()

        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Flyer',
            eventAction: 'open',
            eventLabel: flyer.merchant,
            [Dimension.FlyerStatus.toString()]:
                this.evTracker.engagementStatus(flyer),
            [Dimension.MerchantName.toString()]: flyer.merchant,
            [Dimension.MerchantId.toString()]: flyer.merchantId.toString(),
            [Dimension.FlyerId.toString()]: id,
            [Dimension.Crossbrowse.toString()]: xbrowseId,
            [Metric.Crossbrowse.toString()]: xbrowseId === '' ? 0 : 1,
            nonInteraction: 1,
        })

        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            event: 'flyer_open',
            merchant: flyer.merchant,
            merchantId: flyer.merchantId,
            billing: this.evTracker.engagementStatus(flyer),
            flyerId: id,
            flyerRunId: flyer.flyerRunId,
            flyerTypeId: flyer.flyerTypeId,
        })
        if (this.config.EXPERIENCE === Experience.SDE) {
            window.parent.postMessage(
                {
                    type: SDE_ANALYTIC_MESSAGES.FlyerOpen,
                    data: {
                        flyerId: id,
                        premium: flyer.premium.toString(),
                        merchant: flyer.merchant,
                    },
                },
                '*'
            )
        } else {
            gtag('event', 'conversion', {
                send_to: `${this.config.GTAG_CONVERSION_ID}/${this.config.GTAG_FLYER_OPEN_CONVERSION_LABEL}`,
            })
        }
    }

    trackFlyerEngagement(
        flyer: Flyer,
        sourceAction: AggregateSourceAction,
        xbrowseId?: string
    ) {
        const id = flyer.id.toString()
        // const repeat = this.evTracker.hasEngaged(flyer.id, "ev");
        const status = this.evTracker.engagementStatus(flyer)

        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Flyer',
            eventAction: 'engagement',
            eventLabel: flyer.merchant,
            eventValue: status === 'billable' ? 1 : 0,
            [Dimension.FlyerStatus.toString()]: status,
            [Dimension.MerchantName.toString()]: flyer.merchant,
            [Dimension.MerchantId.toString()]: flyer.merchantId.toString(),
            [Dimension.FlyerId.toString()]: id,
            [Dimension.Crossbrowse.toString()]: xbrowseId,
            [Metric.Crossbrowse.toString()]: xbrowseId === '' ? 0 : 1,
        })

        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            event: 'flyer_engagement',
            merchant: flyer.merchant,
            merchantId: flyer.merchantId,
            billing: this.evTracker.engagementStatus(flyer),
            flyerId: id,
            flyerRunId: flyer.flyerRunId,
            flyerTypeId: flyer.flyerTypeId,
        })
        const label =
            status === 'billable'
                ? this.config.GTAG_PUEV_CONVERSION_LABEL
                : this.config.GTAG_EV_CONVERSION_LABEL
        if (this.config.EXPERIENCE === Experience.SDE) {
            window.parent.postMessage(
                {
                    type: SDE_ANALYTIC_MESSAGES.EV,
                    data: {
                        flyerId: id,
                        premium: flyer.premium.toString(),
                        merchant: flyer.merchant,
                        unique: status === 'billable',
                    },
                },
                '*'
            )
        } else {
            gtag('event', 'conversion', {
                send_to: `${this.config.GTAG_CONVERSION_ID}/${label}`,
            })
        }
    }

    trackFlyerView(
        flyer: Flyer,
        left: number,
        top: number,
        width: number,
        height: number,
        zoomScale: number,
        interaction: number
    ) {}

    trackSearch(
        q: string,
        flyerCount: number,
        tombstoneCount: number,
        itemCount: number,
        ecomItemCount: number,
        couponCount: number,
        couponMatchupCount: number,
        mode: SearchMode
    ) {
        const count = (flyerCount + itemCount + ecomItemCount).toString()

        // sanitized query, follow what app does:
        // 10 apples = apples, 10lb potato = 10lb potato,
        // 2 new york strip steak = new york strip steak
        const sanitizedQuery = q.replace(/^([0-9]+ )/, '')

        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: mode.toString(),
            eventLabel: sanitizedQuery,
            eventValue: count,
            [Dimension.SearchQuery.toString()]: q,
            [Dimension.Context.toString()]: mode,
            [Metric.SearchResult.toString()]: count,
        })

        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            event: 'search',
            query: sanitizedQuery,
            rawQuery: q,
            itemCount: itemCount.toString(),
            flyerCount: flyerCount.toString(),
            ecomCount: ecomItemCount.toString(),
            mode: mode.toString(),
        })
    }

    trackTombstone(q: string, merchant: string, merchantId: number) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'results',
            eventLabel: 'Tombstone',
            [Dimension.SearchQuery.toString()]: q,
            [Dimension.MerchantName.toString()]: merchant,
            [Dimension.MerchantId.toString()]: merchantId.toString(),
        })
    }

    trackSearchItemClick(
        q: string,
        itemId: number,
        flyer: Flyer,
        rank: number
    ) {
        const id = flyer.id.toString()
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'click',
            eventLabel: q,
            eventValue: rank,
            [Dimension.Context.toString()]: 'flyer',
            [Dimension.SearchQuery.toString()]: q,
            [Dimension.MerchantName.toString()]: flyer.merchant,
            [Dimension.MerchantId.toString()]: flyer.merchantId.toString(),
            [Dimension.FlyerId.toString()]: id,
            [Dimension.FlyerStatus.toString()]:
                this.evTracker.engagementStatus(flyer),
        })

        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            event: 'search_click',
            query: q,
            merchant: flyer.merchant,
            merchantId: flyer.merchantId,
            flyerId: id,
            flyerRunId: flyer.flyerRunId,
            flyerTypeId: flyer.flyerTypeId,
            billing: this.evTracker.engagementStatus(flyer),
            itemType: 'flyer',
            itemId: itemId,
            isHidden: 0,
        })
    }

    trackSearchFlyerClick(q: string, flyer: Flyer, rank: number) {
        const id = flyer.id.toString()
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'click',
            eventLabel: q,
            eventValue: rank,
            [Dimension.Context.toString()]: 'merchant_flyer',
            [Dimension.SearchQuery.toString()]: q,
            [Dimension.MerchantName.toString()]: flyer.merchant,
            [Dimension.MerchantId.toString()]: flyer.merchantId.toString(),
            [Dimension.FlyerId.toString()]: id,
            [Dimension.FlyerStatus.toString()]:
                this.evTracker.engagementStatus(flyer),
        })
    }

    trackSearchCouponClick(q: string, coupon: Coupon, rank: number) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'click',
            eventLabel: q,
            eventValue: rank,
            [Dimension.Context.toString()]: 'coupon',
            [Dimension.SearchQuery.toString()]: q,
        })
    }

    trackSearchEcomItemClick(
        q: string,
        itemId: string,
        merchantId: number,
        isHidden: boolean,
        rank: number
    ) {
        const hidden = isHidden ? '1' : '0'

        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'click',
            eventLabel: `${q} | MID ${merchantId} | ITEM ${itemId} | HIDDEN ${hidden}`,
            eventValue: rank,
            [Dimension.Context.toString()]: 'ecom',
            [Dimension.SearchQuery.toString()]: q,
            [Dimension.MerchantName.toString()]: '',
            [Dimension.MerchantId.toString()]: merchantId.toString(),
            [Dimension.FlyerId.toString()]: '',
            [Dimension.FlyerStatus.toString()]: '',
        })

        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            event: 'search_click',
            query: q,
            merchant: '',
            merchantId: merchantId,
            flyerId: '',
            flyerRunId: '',
            flyerTypeId: '',
            billing: '',
            itemType: 'ecom',
            itemId: itemId,
            isHidden: isHidden,
        })
    }

    trackEcomItemTTMClick(itemId: string, merchantId: number) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'click',
            eventLabel: 'TTM',
            [Dimension.ClickSource.toString()]: itemId.toString(),
            [Dimension.MerchantId.toString()]: merchantId.toString(),
        })
    }

    trackCrossBrowse(
        type: CrossBrowseAction,
        from?: Flyer,
        to?: Flyer,
        idx?: number
    ) {
        if (
            type === CrossBrowseAction.NextFlyer ||
            type === CrossBrowseAction.FlyerSuggestion
        ) {
            this.nextXBrowseFromOpen = from
            this.nextXBrowseFromEngagement = from
        }

        let eventLabel = ''
        switch (type) {
            case CrossBrowseAction.NextFlyer:
                eventLabel = 'Next Flyer button'
                break
            case CrossBrowseAction.FlyerSuggestion:
                eventLabel = `Flyer Suggestion ${idx}`
                break
            case CrossBrowseAction.SeeAllFlyers:
                eventLabel = 'See Flyers button'
                break
            case CrossBrowseAction.TopLogo:
                eventLabel = 'Top Logo'
                break
        }

        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Flyer View',
            eventAction: 'Cross Browse',
            eventLabel,
        })
    }

    trackMobileInstall(device: string, category: string) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: category,
            eventAction: 'Install',
            eventLabel: device,
        })
    }

    trackCouponPrint() {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Coupons',
            eventAction: 'print',

            [Dimension.CouponType.toString()]: 'print',
        })
    }

    trackFlyerItem(flyer: Flyer, itemId: number, st: number) {
        const id = flyer.id.toString()
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Item',
            eventAction: 'click',
            eventLabel: FLYER_ITEM_CLICK_TYPE[st],
            eventValue: 0,
            [Dimension.MerchantName.toString()]: flyer.merchant,
            [Dimension.MerchantId.toString()]: flyer.merchantId?.toString(),
            [Dimension.FlyerId.toString()]: id,
        })

        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            event: 'item_interaction',
            itemId: itemId.toString(),
            flyerId: id,
            flyerRunId: flyer.flyerRunId,
            flyerTypeId: flyer.flyerTypeId,
            merchant: flyer.merchant,
            merchantId: flyer.merchantId,
            action: FLYER_ITEM_CLICK_TYPE[st],
        })
    }

    trackEcomItem(
        itemId: string,
        merchantId: number,
        merchant: string,
        st: number,
        isHidden: boolean
    ) {
        // NOTE: This is the format that Guenia has in her Google Doc for GA.
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Ecom Item',
            eventAction: FLYER_ITEM_CLICK_TYPE[st],
            eventLabel: `MER ${merchant} | MID ${merchantId} | ITEM ${itemId}`,
            eventValue: isHidden ? 1 : 0,
            [Dimension.Context.toString()]: 'ecom',
            [Dimension.MerchantName.toString()]: merchant,
            [Dimension.MerchantId.toString()]: merchantId.toString(),
        })

        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            event: 'ecom_item_interaction',
            itemType: 'ecom',
            itemId: itemId,
            flyerId: '',
            flyerRunId: '',
            flyerTypeId: '',
            merchant: merchant,
            merchantId: merchantId,
            isHidden: isHidden,
            action: FLYER_ITEM_CLICK_TYPE[st],
        })
    }

    trackCoupon(
        coupon: Coupon,
        st: CouponActions,
        src: CouponSource,
        flyerId?: number,
        programMerchant?: LoyaltyProgramMerchant,
        loyaltyCoupon?: LoyaltyProgramCoupon
    ) {
        const merchantId = programMerchant
            ? String(programMerchant.merchantId)
            : coupon.merchantId
              ? String(coupon.merchantId)
              : ''
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Coupons',
            eventAction: COUPON_ACTIONS[st],

            [Dimension.CouponId.toString()]: String(coupon.id),
            [Dimension.MerchantId.toString()]: String(merchantId),
            [Dimension.CouponVendorId.toString()]:
                coupon.vendorId !== null ? String(coupon.vendorId) : '',
            [Dimension.CouponType.toString()]: coupon.type
                ? coupon.type.toString()
                : '',
            [Dimension.Brand.toString()]: coupon.brand,
            [Dimension.RedemptionMethod.toString()]:
                coupon.redemptionMethod.toString(),
            [Dimension.ClickSource.toString()]: src.toString(),
            [Dimension.LoyaltyProgramId.toString()]: loyaltyCoupon
                ? String(loyaltyCoupon.loyaltyProgramId)
                : '',
            [Dimension.LoyaltyCouponId.toString()]: loyaltyCoupon
                ? String(loyaltyCoupon.id)
                : '',
        })
    }

    trackVariant(experiment: Experiment, value: string) {
        this.trackGA('send', {
            hitType: 'event',

            eventCategory: 'Experiment',
            eventAction: 'variant',
            eventLabel: experiment,
            [Dimension.Variant.toString()]: value,
            nonInteraction: 1,
        })
    }

    trackSignin(
        userGuid: string,
        provider: AuthProvider,
        context: LoginSource
    ) {
        const accounts = ['google', 'facebook']
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Authentication',
            eventAction: 'login',
            eventLabel: accounts[provider],
            [Dimension.Context.toString()]: context.toString(),
            [Dimension.UserId.toString()]: userGuid,
        })
    }

    trackSignout(userGuid: string, provider: AuthProvider) {
        const accounts = ['google', 'facebook']
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Authentication',
            eventAction: 'logout',
            eventLabel: accounts[provider],
            [Dimension.UserId.toString()]: userGuid,
        })
    }

    trackUnsupportedBrowserErrorPage() {
        this.trackGA(
            'send',
            {
                hitType: 'event',
                eventCategory: 'Error',
                eventAction: 'UpdateBrowser',
                eventLabel: 'errorPage_browser',
                [Dimension.Context.toString()]: window.location.href,
                nonInteraction: 1,
            },
            TRACKER_BASE,
            true
        )
    }

    trackFatalRuntimeErrorPage() {
        this.trackGA(
            'send',
            {
                hitType: 'event',
                eventCategory: 'Error',
                eventAction: 'FatalError',
                eventLabel: 'errorPage_fatal',
                [Dimension.Context.toString()]: window.location.href,
                nonInteraction: 1,
            },
            TRACKER_BASE,
            true
        )
    }

    trackWebGLDisabled() {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Error',
            eventAction: 'WebGLError',
            eventLabel: window.location.href,
            nonInteraction: 1,
        })
    }

    trackLoyaltyCardAdded(program: LoyaltyProgram, context: LoyaltyCardSource) {
        const sourceMap: { [key: number]: string } = {
            [LoyaltyCardSource.CouponDetails]: 'coupon_details',
            [LoyaltyCardSource.FlyerItem]: 'flyer_item',
            [LoyaltyCardSource.CouponPage]: 'coupon_page',
        }

        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Loyalty Card',
            eventAction: 'add',
            eventLabel: sourceMap[context],
            eventValue: program.id,
            [Dimension.ProgramValidationType.toString()]:
                program.validationMethod,
        })
    }

    trackLoyaltyCardRemoved(programId: number) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Loyalty Card',
            eventAction: 'remove',
            eventValue: programId,
        })
    }

    trackPageNotFoundEvent() {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Error',
            eventAction: 'Page Not Found',
            eventLabel: window.location.href,
            eventValue: 404,
            [Dimension.Referer.toString()]: document.referrer || 'direct',
            nonInteraction: 1,
        })
    }

    trackShoppingListChanged(
        item: string,
        listId: string,
        action: ShoppingListAction,
        context: ShoppingListEventContext
    ) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Shopping List',
            eventAction: action.toString(),
            eventLabel: item,
            [Dimension.ShoppingListId.toString()]: listId,
            [Dimension.ShoppingListContext.toString()]: context.toString(),
        })
    }

    trackShoppingListPhotoDeleted(listId: string) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Shopping List',
            eventAction: 'SL Photo Delete',
            [Dimension.ShoppingListId.toString()]: listId,
        })
    }

    trackShoppingListRecommendedStream(
        action: string,
        stream: string,
        listId: string,
        item: string
    ) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Shopping List',
            eventAction: action,
            eventLabel: item,
            [Dimension.ShoppingListId.toString()]: listId,
            [Dimension.ShoppingListContext.toString()]: stream,
        })
    }

    trackSearchMiss(item: string, listId: string) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Shopping List',
            eventAction: 'searchMiss',
            eventLabel: item,
            eventValue: 0,
            [Dimension.ShoppingListId.toString()]: listId,
        })
    }

    trackAutocompleteHit(
        q: string,
        term: string,
        rank: number,
        context: string
    ) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'autocomplete',
            eventLabel: term,
            eventValue: rank,
            [Dimension.SearchQuery.toString()]: q,
            [Dimension.Context.toString()]: context,
        })
    }

    trackShoppingListPrint(listId: string, action: ShoppingListAction) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Shopping List',
            eventAction: action.toString(),
        })
    }

    trackShoppingListEvent(
        listId: string,
        action: ShoppingListAction,
        numAffected: number
    ) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Shopping List',
            eventAction: action.toString(),
            eventValue: numAffected,
        })
    }

    trackFamilySyncEvent(
        listId: string,
        action: FamilySyncAction,
        inviteId?: string
    ) {
        const payload: { [key: string]: string } = {
            st: action.toString(),
        }
        if (listId) {
            payload['shopping_list_id'] = listId
        }
        if (inviteId) {
            payload['invite_id'] = inviteId
        }

        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Shopping List',
            eventAction: action.toString(),
            eventLabel: inviteId,
            [Dimension.ShoppingListId.toString()]: listId,
        })
    }

    trackFavouriteEvent(
        merchantId: number,
        action: FavouriteAction,
        context: FavouriteContext
    ) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Favourite',
            eventAction: action,
            eventLabel: merchantId,
            [Dimension.Context.toString()]: context.toString(),
        })
    }

    trackOldBrowser() {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Experience',
            eventAction: 'UpdateBrowser',
            eventLabel: 'updateBrowserOverlay',
            [Dimension.Context.toString()]: window.location.href,
            nonInteraction: 1,
        })
    }

    trackSearchFiltering(count: number, sortOrder: string) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'Filter changed',
            eventLabel: sortOrder,
            eventValue: count.toString(),
        })
    }

    trackAdditionalResultsClick(count: number) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Search',
            eventAction: 'Show more results clicked',
            eventValue: count.toString(),
        })
    }

    trackFlyerListing(
        categoryName: string,
        flyers: Flyer[],
        sortOrder: string,
        favMerchants?: number[]
    ) {
        const flyerIds: string[] = this.generateFlyerIds(
            categoryName,
            flyers,
            favMerchants
        )
        const flyerCount = flyerIds.length
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Flyer Listing',
            eventAction: categoryName,
            eventLabel: sortOrder,
            eventValue: flyerCount,
        })
    }

    trackPublisherBackButtonClick(publisherName: string) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Experience',
            eventAction: 'click',
            eventLabel: 'Publisher Back Button',
            [Dimension.PublisherName.toString()]: publisherName,
            [Dimension.PublisherChannelId.toString()]: this.channelId,
        })
    }

    trackPublisherBackButtonVisible(publisherName: string) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Experience',
            eventAction: 'visible',
            eventLabel: 'Publisher Back Button',
            [Dimension.PublisherName.toString()]: publisherName,
            [Dimension.PublisherChannelId.toString()]: this.channelId,
        })
    }

    trackFlyerClick(
        category: string,
        sort: string,
        placement: FlyerPlacement,
        position: number
    ) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Flyer Clicked',
            eventAction: category,
            eventLabel: `SORT ${sort} | PLACE ${placement.toString()}`,
            eventValue: position,
        })
    }

    trackCouponListing(
        categoryName: string,
        coupons: Coupon[],
        sortOrder: string
    ) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Coupon Listing',
            eventAction: categoryName,
            eventLabel: sortOrder,
            eventValue: coupons.length,
        })
    }

    trackLandingPage(st: LandingPageActions, q?: string, flyer?: Flyer) {
        const query: string = q ? q : ''

        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Landing Page',
            eventAction: LANDING_PAGE_ACTIONS[st],
            eventLabel: LANDING_PAGE_VERSION,
            [Dimension.MerchantName.toString()]: flyer ? flyer.merchant : '',
            [Dimension.MerchantId.toString()]: flyer ? flyer.merchantId : '',
            [Dimension.FlyerId.toString()]: flyer ? `${flyer.id}` : '',
            [Dimension.SearchQuery.toString()]: query,
        })
    }

    trackLocationChanges(oldFsa: string, newFsa: string) {
        this.trackGA('send', {
            hitType: 'event',
            eventCategory: 'Location Change',
            eventAction: oldFsa,
            eventLabel: newFsa,
        })
    }

    trackShoppingListView(
        items: number,
        photos: number,
        flyerItems: number,
        ecomItems: number,
        coupon: number
    ) {}

    trackFlyerClose(flyer: Flyer, sourceAction: AggregateSourceAction) {}

    trackWindowClose() {}

    protected getXBrowseOpen = () => {
        // Figure out what to send for the cross browse dimension
        let xBrowseFrom = ''
        if (
            this.nextXBrowseFromOpen &&
            this.nextXBrowseFromOpen === this.nextXBrowseFromEngagement
        ) {
            xBrowseFrom = String(this.nextXBrowseFromOpen.id)
            this.nextXBrowseFromOpen = null
        } else {
            this.nextXBrowseFromEngagement = null
        }
        return xBrowseFrom
    }

    protected getXBrowseEngagement = () => {
        // Figure out what to send for the cross browse dimension
        let xBrowseFrom = ''
        if (this.nextXBrowseFromEngagement) {
            xBrowseFrom = String(this.nextXBrowseFromEngagement.id)
            this.nextXBrowseFromEngagement = null
        }
        return xBrowseFrom
    }
}
